// SCR-STAFF-879 (AP-INV-02؛ US-INV-040، BR-INV-030 تا 033): تنظیمات انبار — روش ارزش‌گذاری (میانگین متحرک یا FIFO) که تغییرش فقط از ابتدای
// سال مالی بعد مؤثر است (PHX-INV-0309)، آستانه نزدیک به انقضا و سقف ضایعات بدون تأیید؛ AP-INV-03: سقف تأیید درخواست خرید (BR-INV-050) و آستانه
// هشدار اختلاف قیمت رسید با سفارش (C-AGENT-1242، 1246).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type InvInventorySettingsView, type InvValuationMethod } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, latinDigits, toJalali, ListPage, LoadingState, MoneyFieldRHF } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { WriteNotice } from '../settings/common';
import { stockKeys } from './stockCommon';

export const SCR_STAFF_879 = 'SCR-STAFF-879';

const METHODS: readonly InvValuationMethod[] = ['MovingAverage', 'Fifo'];

interface SettingsForm {
  method: InvValuationMethod;
  nearExpiryDays: string;
  wasteApprovalLimit: number | null;
  purchaseApprovalLimit: number | null;
  priceVariancePercent: string;
}

export function InventorySettingsPage() {
  const { tenantApi, session } = useTenantSession();
  const settings = useQuery({
    queryKey: stockKeys.settings(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/settings')),
  });
  return (
    <ListPage screenId={SCR_STAFF_879} titleKey="inv:settings.title" subtitleKey="inv:settings.subtitle">
      {settings.isPending && <LoadingState rows={3} />}
      {settings.error && <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />}
      {settings.data && <SettingsEditor view={settings.data} />}
    </ListPage>
  );
}

function formDefaults(source: InvInventorySettingsView): SettingsForm {
  return {
    method: source.pendingMethod ?? source.method,
    nearExpiryDays: String(source.nearExpiryDays),
    wasteApprovalLimit: Number(source.wasteApprovalLimit.amount),
    purchaseApprovalLimit: Number(source.purchaseApprovalLimit.amount),
    priceVariancePercent: String(source.priceVariancePercent),
  };
}

function SettingsEditor({ view }: { view: InvInventorySettingsView }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('inv.settings.manage');
  const [saved, setSaved] = useState(false);
  const { control, register, handleSubmit, watch, reset } = useForm<SettingsForm>({ defaultValues: formDefaults(view) });
  useEffect(() => reset(formDefaults(view)), [view, reset]);
  const method = watch('method');
  const changesMethod = method !== view.method;

  const save = useMutation({
    mutationFn: (form: SettingsForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/inventory/settings', {
          body: {
            method: form.method,
            effectiveFrom: form.method !== view.method ? view.nextFiscalYearStart : null,
            nearExpiryDays: Number(form.nearExpiryDays),
            wasteApprovalLimit: { amount: form.wasteApprovalLimit ?? 0, currency: view.wasteApprovalLimit.currency },
            purchaseApprovalLimit: { amount: form.purchaseApprovalLimit ?? 0, currency: view.purchaseApprovalLimit.currency },
            priceVariancePercent: Number(latinDigits(form.priceVariancePercent)),
          },
        }),
      ),
    onMutate: () => setSaved(false),
    onSuccess: (updated) => {
      queryClient.setQueryData(stockKeys.settings(session?.tenantId), updated);
      setSaved(true);
    },
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, maxWidth: 640 }} component="form" onSubmit={handleSubmit((form) => save.mutate(form))}>
      <Stack spacing={2}>
        <WriteNotice write={write} />
        <Alert severity="info" data-testid="settings-current-method">
          {t('inv:settings.current', { method: t(`inv:valuation_method.${view.method}`) })}
          {view.pendingMethod && view.pendingFrom ? ` ${t('inv:settings.pending', { method: t(`inv:valuation_method.${view.pendingMethod}`), date: toJalali(view.pendingFrom) })}` : ''}
        </Alert>
        <TextField select label={t('inv:settings.method')} value={method} {...register('method')} disabled={!write.canWrite} slotProps={{ htmlInput: { 'data-testid': 'settings-method' } }}>
          {METHODS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`inv:valuation_method.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        {changesMethod && (
          <Alert severity="warning" data-testid="settings-method-effective">
            {t('inv:settings.effective_from', { date: toJalali(view.nextFiscalYearStart) })}
          </Alert>
        )}
        <TextField
          label={t('inv:settings.near_expiry_days')}
          {...register('nearExpiryDays')}
          disabled={!write.canWrite}
          helperText={t('inv:settings.near_expiry_help', { days: faDigits(String(view.nearExpiryDays)) })}
          slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', 'data-testid': 'settings-near-expiry' } }}
        />
        <MoneyFieldRHF control={control} name="wasteApprovalLimit" labelKey="inv:settings.waste_limit" disabled={!write.canWrite} />
        <MoneyFieldRHF control={control} name="purchaseApprovalLimit" labelKey="inv:settings.purchase_approval_limit" helperKey="inv:settings.purchase_approval_limit_help" disabled={!write.canWrite} />
        <TextField
          label={t('inv:settings.price_variance_percent')}
          {...register('priceVariancePercent')}
          disabled={!write.canWrite}
          helperText={t('inv:settings.price_variance_help')}
          slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', 'data-testid': 'settings-price-variance' } }}
        />
        {save.error && <ErrorState error={save.error} />}
        {saved && <Alert severity="success">{t('inv:settings.saved')}</Alert>}
        <Stack direction="row">
          <Button type="submit" variant="contained" disabled={!write.canWrite || save.isPending} data-testid="settings-save">
            {t('common:actions.save')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
